/**
 * Agent Ring Types
 * Types for the Agent Ring visualization component (Story 12.1.1)
 * Part of Epic 12: UI/UX & Marketing Transformation
 */

export type TrustTier = 1 | 2 | 3 | 4 | 5;

export type AgentStatus = 'active' | 'pending' | 'monitoring' | 'idle' | 'error';

export interface AgentRingAgent {
  id: string;
  name: string;
  shortName: string; // For display in tight spaces
  trustTier: TrustTier;
  status: AgentStatus;
  tasksCompletedToday: number;
  lastActivity?: string;
  icon: string; // Lucide icon name
  description: string;
  category: string; // e.g., 'SEO', 'Content', 'Social', etc.
}

export interface TrustTierConfig {
  tier: TrustTier;
  name: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  ringRadius: number; // Percentage from center
}

export interface AgentRingProps {
  agents: AgentRingAgent[];
  selectedAgentId?: string | null;
  onAgentClick?: (agent: AgentRingAgent) => void;
  onAgentHover?: (agent: AgentRingAgent | null) => void;
  size?: 'sm' | 'md' | 'lg';
  showLabels?: boolean;
  animated?: boolean;
  className?: string;
}

export interface AgentTooltipData {
  agent: AgentRingAgent;
  position: { x: number; y: number };
}

// Trust Tier configurations matching the 5-tier model from patents
export const TRUST_TIER_CONFIG: Record<TrustTier, TrustTierConfig> = {
  1: {
    tier: 1,
    name: 'Auto-Pilot',
    description: 'Full autonomy - executes without approval',
    color: '#22c55e', // Green
    bgColor: 'rgba(34, 197, 94, 0.2)',
    borderColor: 'rgba(34, 197, 94, 0.5)',
    ringRadius: 85, // Outermost ring
  },
  2: {
    tier: 2,
    name: 'Co-Pilot',
    description: 'Light oversight - periodic review required',
    color: '#3b82f6', // Blue
    bgColor: 'rgba(59, 130, 246, 0.2)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
    ringRadius: 70,
  },
  3: {
    tier: 3,
    name: 'Assistant',
    description: 'Human review required before execution',
    color: '#f59e0b', // Amber
    bgColor: 'rgba(245, 158, 11, 0.2)',
    borderColor: 'rgba(245, 158, 11, 0.5)',
    ringRadius: 55,
  },
  4: {
    tier: 4,
    name: 'Research',
    description: 'Provides recommendations only',
    color: '#a855f7', // Purple
    bgColor: 'rgba(168, 85, 247, 0.2)',
    borderColor: 'rgba(168, 85, 247, 0.5)',
    ringRadius: 40,
  },
  5: {
    tier: 5,
    name: 'Observer',
    description: 'Monitoring and alerting only',
    color: '#6b7280', // Gray
    bgColor: 'rgba(107, 114, 128, 0.2)',
    borderColor: 'rgba(107, 114, 128, 0.5)',
    ringRadius: 25, // Innermost ring
  },
};

// Status colors for agent indicators
export const STATUS_COLORS: Record<AgentStatus, { color: string; pulse: boolean; label: string }> = {
  active: { color: '#22c55e', pulse: true, label: 'Active' },
  pending: { color: '#f59e0b', pulse: true, label: 'Pending Approval' },
  monitoring: { color: '#3b82f6', pulse: false, label: 'Monitoring' },
  idle: { color: '#6b7280', pulse: false, label: 'Idle' },
  error: { color: '#ef4444', pulse: true, label: 'Error' },
};

// The 16 agents organized by trust tier
export const DEFAULT_AGENTS: AgentRingAgent[] = [
  // Tier 1: Auto-Pilot (3 agents)
  { id: 'analytics', name: 'Analytics Agent', shortName: 'Analytics', trustTier: 1, status: 'active', tasksCompletedToday: 45, icon: 'BarChart3', description: 'Tracks and reports marketing performance', category: 'Analytics' },
  { id: 'technical-seo', name: 'Technical SEO Agent', shortName: 'Tech SEO', trustTier: 1, status: 'active', tasksCompletedToday: 23, icon: 'Search', description: 'Monitors and optimizes technical SEO', category: 'SEO' },
  { id: 'local-seo', name: 'Local SEO Agent', shortName: 'Local SEO', trustTier: 1, status: 'monitoring', tasksCompletedToday: 12, icon: 'MapPin', description: 'Manages local search presence', category: 'SEO' },

  // Tier 2: Co-Pilot (5 agents)
  { id: 'seo-keyword', name: 'SEO Keyword Agent', shortName: 'Keywords', trustTier: 2, status: 'active', tasksCompletedToday: 34, icon: 'Key', description: 'Researches and optimizes keywords', category: 'SEO' },
  { id: 'content-strategy', name: 'Content Strategy Agent', shortName: 'Content', trustTier: 2, status: 'active', tasksCompletedToday: 18, icon: 'FileText', description: 'Plans and creates content strategy', category: 'Content' },
  { id: 'email-automation', name: 'Email Automation Agent', shortName: 'Email', trustTier: 2, status: 'pending', tasksCompletedToday: 8, icon: 'Mail', description: 'Automates email marketing campaigns', category: 'Email' },
  { id: 'social-scheduler', name: 'Social Scheduler Agent', shortName: 'Social', trustTier: 2, status: 'active', tasksCompletedToday: 56, icon: 'Share2', description: 'Schedules and posts social content', category: 'Social' },
  { id: 'video-content', name: 'Video Content Agent', shortName: 'Video', trustTier: 2, status: 'monitoring', tasksCompletedToday: 5, icon: 'Video', description: 'Creates and optimizes video content', category: 'Content' },

  // Tier 3: Assistant (5 agents)
  { id: 'backlink-builder', name: 'Backlink Builder Agent', shortName: 'Backlinks', trustTier: 3, status: 'active', tasksCompletedToday: 7, icon: 'Link', description: 'Builds and manages backlink profiles', category: 'SEO' },
  { id: 'pr-outreach', name: 'PR Outreach Agent', shortName: 'PR', trustTier: 3, status: 'pending', tasksCompletedToday: 3, icon: 'Newspaper', description: 'Manages press and media outreach', category: 'PR' },
  { id: 'ad-optimizer', name: 'Ad Optimizer Agent', shortName: 'Ads', trustTier: 3, status: 'active', tasksCompletedToday: 22, icon: 'Target', description: 'Optimizes advertising campaigns', category: 'Ads' },
  { id: 'ppc-agent', name: 'PPC Agent', shortName: 'PPC', trustTier: 3, status: 'monitoring', tasksCompletedToday: 15, icon: 'DollarSign', description: 'Manages pay-per-click campaigns', category: 'Ads' },
  { id: 'influencer-connect', name: 'Influencer Connect Agent', shortName: 'Influencer', trustTier: 3, status: 'idle', tasksCompletedToday: 2, icon: 'Users', description: 'Connects with influencers', category: 'Partnerships' },

  // Tier 4: Research (2 agents)
  { id: 'podcast-agent', name: 'Podcast Agent', shortName: 'Podcast', trustTier: 4, status: 'idle', tasksCompletedToday: 1, icon: 'Mic', description: 'Plans podcast appearances and content', category: 'Content' },
  { id: 'conversion-agent', name: 'Conversion Agent', shortName: 'Conversion', trustTier: 4, status: 'active', tasksCompletedToday: 9, icon: 'TrendingUp', description: 'Analyzes and improves conversions', category: 'Analytics' },

  // Tier 5: Observer (1 agent)
  { id: 'mobile-marketing', name: 'Mobile Marketing Agent', shortName: 'Mobile', trustTier: 5, status: 'monitoring', tasksCompletedToday: 0, icon: 'Smartphone', description: 'Monitors mobile marketing trends', category: 'Research' },
];
